import { Box, Button, Collapse, Icon, List, ListIcon, ListItem } from '@chakra-ui/react'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useLanguage } from '../../hooks/utility/useLanguage'

// Elements whose keys all belong to them (typing, a menu's arrows), not to scrolling the chat.
const OWNS_KEYS =
  'input, textarea, select, [contenteditable="true"], ' +
  '[role="menu"], [role="menuitem"], [role="listbox"], [role="option"], [role="radiogroup"], [role="tablist"]'
const SCROLL_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']

interface WebSearchQueriesProps {
  // After the user's edits in the approval card.
  queries: string[]
}

const WebSearchQueries: React.FC<WebSearchQueriesProps> = ({ queries }) => {
  const { t } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)

  // The header this line sits in stays in view while the answer scrolls: an open list
  // would cover the text, so the user scrolling closes it. Wheel, touch and keys rather
  // than `scroll`: opening the list itself makes the chat scroll.
  useEffect(() => {
    if (!isOpen) return
    const close = () => setIsOpen(false)
    const closeOnScrollKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target?.closest(OWNS_KEYS)) return
      // A focused button's Space presses it (this toggle reopening the list); other keys scroll.
      if (event.key === ' ' && target?.closest('button, a, [role="button"]')) return
      if (SCROLL_KEYS.includes(event.key)) close()
    }
    window.addEventListener('wheel', close, { passive: true })
    window.addEventListener('touchmove', close, { passive: true })
    window.addEventListener('keydown', closeOnScrollKey)
    return () => {
      window.removeEventListener('wheel', close)
      window.removeEventListener('touchmove', close)
      window.removeEventListener('keydown', closeOnScrollKey)
    }
  }, [isOpen])

  return (
    <Box>
      <Button
        variant='unstyled'
        size='sm'
        height='auto'
        display='inline-flex'
        alignItems='center'
        gap={0.5}
        color='chat.muted'
        fontSize='xs'
        fontWeight='normal'
        _hover={{ color: 'chat.ink', textDecoration: 'underline' }}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
      >
        {t('internetSearch.queriesSent', { count: queries.length })}
        <Icon as={isOpen ? ChevronUp : ChevronDown} boxSize={3} />
      </Button>
      <Collapse in={isOpen} animateOpacity>
        <List spacing={0.5} pt={0.5} color='chat.muted' fontSize='xs'>
          {queries.map((query, i) => (
            <ListItem key={i} overflowWrap='anywhere'>
              <ListIcon as={Search} boxSize={2.5} />
              {query}
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  )
}

export default WebSearchQueries
